import { useState, type ReactNode } from 'react';
import { CuadroDeBusqueda } from '../Buscable';
import { filtrarFilas } from '../busqueda';
import { useQuery } from '@tanstack/react-query';

import { almacen, esReventa, type ExtraRecipe, type PendingRecipe, type Recipe } from '../api';
import {
  Cabecera,
  Cargando,
  Cifra,
  ConAyuda,
  Fallo,
  filaPulsable,
  Filtro,
  Pastilla,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, contados, porcentaje, puntos, soles } from '../formato';
import { NuevaReceta, VincularReventa, VincularReventaEnBloque } from '../formularios';

/** Los objetivos de food cost con los que se juega al fijar un precio. */
const OBJETIVOS = [25, 28, 30, 33];

/**
 * El escandallo de cada plato: qué lleva, qué cuesta y qué margen deja.
 *
 * El costo sale del stock de **este local**, no de un precio guardado en la
 * receta: lo que vale un kilo de lomo cambia con cada compra, y una ficha
 * técnica con el precio de hace tres meses enseña un margen que ya no existe.
 */
export function Recetas() {
  const recetas = useQuery({ queryKey: ['recetas'], queryFn: almacen.recipes });
  const pendientes = useQuery({
    queryKey: ['recetas', 'pendientes'],
    queryFn: almacen.pendingRecipes,
  });
  const extras = useQuery({
    queryKey: ['recetas', 'extras'],
    queryFn: almacen.extraRecipes,
  });

  const [elegida, setElegida] = useState<string | null>(null);
  const [objetivo, setObjetivo] = useState(30);
  const [texto, setTexto] = useState('');

  const lista = recetas.data ?? [];
  const receta = lista.find((r) => r.id === elegida) ?? lista[0];

  /*
   * Cada plato nace en la carta con su receta vacía, y una vacía no tiene food
   * cost: cuesta cero y enseñaría «0 %, en meta». Contarla bajaba el promedio
   * de la carta y metía entre las activas lo que todavía no descuenta nada.
   */
  const llenas = lista.filter((r) => r.lines.length > 0);
  const porLlenar = lista.filter((r) => r.lines.length === 0);

  const promedio =
    llenas.length === 0
      ? 0
      : llenas.reduce((suma, r) => suma + r.foodCost, 0) / llenas.length;

  const sobreMeta = llenas.filter((r) => r.foodCost > 30);

  /*
   * La misma deuda con el almacén, pero se salda distinto: un plato pide
   * escandallo y una gaseosa pide una línea. Mezclarlas hacía que la lista de
   * «recetas por cargar» pareciera cuarenta escandallos por escribir, que es
   * justo la razón por la que nadie la empezaba.
   */
  /*
   * Lo que hoy no sale, y por culpa de qué.
   *
   * No apaga el plato: eso lo hace una persona con el interruptor de la carta.
   * El libro puede estar equivocado —nadie registró la entrega de la mañana— y
   * quitar de la carta pescado que está en la cámara es peor que venderlo. Esto
   * le da a esa persona el dato que le faltaba, con el nombre del ingrediente:
   * «no sale porque falta culantro» se resuelve mandando a alguien al mercado.
   */
  const noSalen = lista.filter((r) => r.possiblePortions === 0 && r.limitedBy);

  const pendiente = pendientes.data ?? [];
  const porCocinar = pendiente.filter((p) => !esReventa(p));
  const porVincular = pendiente.filter(esReventa);

  return (
    <>
      <Cabecera
        titulo="Recetas y costeo"
        subtitulo="Escandallo por plato, food cost y margen" ayuda="Qué lleva cada plato y cuánto cuesta hacerlo. Con la receta cargada, cada venta descuenta sus ingredientes del almacén."
        acciones={
          <>
            <VincularReventaEnBloque />
            <NuevaReceta />
          </>
        }
      />

      <div className="cuerpo">
        {recetas.isError ? (
          <Fallo error={recetas.error} onReintentar={() => recetas.refetch()} />
        ) : null}

        <div className="cifras">
          <Cifra
            rotulo="Food cost promedio"
            ayuda="En promedio, qué parte del precio de los platos se va en ingredientes."
            valor={porcentaje(promedio)}
            apoyo="meta de la carta: 30 %"
          />
          <Cifra
            rotulo="Platos sobre meta"
            ayuda="Platos cuyo food cost pasa de la meta: dejan menos margen del que se quiere."
            valor={String(sobreMeta.length)}
            apoyo={
              sobreMeta.length === 0
                ? 'ninguno se come el margen'
                : sobreMeta
                    .slice(0, 3)
                    .map((r) => r.name)
                    .join(', ')
            }
            tono={sobreMeta.length > 0 ? 'aviso' : 'neutro'}
          />
          <Cifra
            rotulo="Recetas activas"
            ayuda="Platos que ya descuentan del almacén al venderse."
            valor={String(llenas.length)}
            apoyo={
              porLlenar.length === 0
                ? 'con escandallo cargado'
                : `${contados(porLlenar.length, 'plato', 'platos')} con la receta por llenar`
            }
            tono={porLlenar.length > 0 ? 'aviso' : 'neutro'}
          />
          <Cifra
            rotulo="Sin receta"
            ayuda="Platos que se venden y no descuentan nada porque no tienen receta cargada."
            valor={String((pendientes.data ?? []).length)}
            apoyo={
              (pendientes.data ?? []).length === 0
                ? 'todo lo que se vende descuenta'
                : 'se venden y no descuentan nada'
            }
            tono={(pendientes.data ?? []).length > 0 ? 'peligro' : 'neutro'}
          />
        </div>

        {noSalen.length > 0 ? (
          <Tarjeta>
            <h2 className="tarjeta__titulo">
              Hoy no salen{' '}
              <span className="mono apagado">({noSalen.length})</span>
            </h2>
            <p className="nota">
              Con lo que hay en el local, estos platos no se pueden preparar.
              Siguen en la carta: el almacén avisa, no apaga. Quitarlos —o no—
              lo decide quien esté en el local, porque a veces lo que falta está
              en la cámara y nadie registró la entrega.
            </p>
            <ul className="lista">
              {noSalen.map((receta) => (
                <li key={receta.id} className="lista__fila">
                  <div className="lista__texto">
                    <strong>{receta.name}</strong>
                    <span className="apagado">Falta {receta.limitedBy}</span>
                  </div>
                  <Pastilla texto="Sin ingredientes" tono="aviso" />
                </li>
              ))}
            </ul>
          </Tarjeta>
        ) : null}

        {porCocinar.length > 0 ? (
          <Deuda
            titulo="Platos sin escandallo"
            explicacion="Estos se preparan aquí y no descuentan nada del almacén. No bloquean la venta —nadie se queda sin cobrar por un escandallo que falta— pero mientras estén en esta lista, el conteo no va a cuadrar, y este es el motivo."
            etiqueta="Falta receta"
            items={porCocinar}
            accion={<NuevaReceta />}
          />
        ) : null}

        {porVincular.length > 0 ? (
          <Deuda
            titulo="Reventa sin vincular"
            explicacion="Estos se compran hechos y se venden tal cual, así que no llevan escandallo: basta decir de qué insumo salen y cuánto gasta cada venta. Es un minuto por producto, y hasta que se haga, cada lata que sale del refrigerador se queda en el sistema."
            etiqueta="Falta vínculo"
            items={porVincular}
            accion={
              <>
                <VincularReventaEnBloque />
                <VincularReventa />
              </>
            }
          />
        ) : null}

        {(extras.data ?? []).length > 0 ? <Extras extras={extras.data ?? []} /> : null}

        {recetas.isLoading ? (
          <Cargando que="las recetas" />
        ) : lista.length === 0 ? (
          <Vacio
            titulo="Ninguna receta cargada"
            mensaje="Con el escandallo de cada plato, vender descuenta los insumos solo y el almacén deja de llevarse a mano. Sin él, cada salida hay que registrarla una por una."
            accion={<NuevaReceta />}
          />
        ) : (
          <div className="dos-columnas">
            <div className="tabla-envoltura">
              <div className="filtros">
                <CuadroDeBusqueda
                  valor={texto}
                  onCambio={setTexto}
                  placeholder="Buscar plato…"
                  etiquetaAccesible="Buscar plato"
                />
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Plato</th>
                    <th className="numero">Precio</th>
                    <th className="numero"><ConAyuda ayuda="Lo que cuestan los ingredientes de un plato, con la merma incluida.">Costo</ConAyuda></th>
                    <th className="numero"><ConAyuda ayuda="Cuántos platos se pueden hacer con lo que hay en el almacén ahora.">Salen hoy</ConAyuda></th>
                    <th><ConAyuda ayuda="Qué parte del precio se va en ingredientes. Por encima de la meta, el plato deja poco.">Food cost</ConAyuda></th>
                    <th className="numero"><ConAyuda ayuda="Lo que queda del precio después de pagar los ingredientes.">Margen</ConAyuda></th>
                  </tr>
                </thead>
                <tbody>
                  {filtrarFilas(lista, texto, (r) => [r.name]).map((r) => (
                    <tr
                      key={r.id}
                      {...filaPulsable(() => setElegida(r.id))}
                      className={`fila--pulsable ${
                        r.id === receta?.id ? 'fila--activa' : ''
                      }`}
                      aria-current={r.id === receta?.id ? 'true' : undefined}
                    >
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{r.name}</strong>
                          <span>
                            {r.lines.length === 0
                              ? 'sin ingredientes todavía'
                              : contados(r.lines.length, 'ingrediente', 'ingredientes')}
                          </span>
                        </div>
                      </td>
                      <td className="numero">{soles(r.price)}</td>
                      {r.lines.length === 0 ? (
                        <>
                          <td className="numero apagado">—</td>
                          <td className="numero apagado">—</td>
                          <td>
                            <Pastilla texto="Por llenar" tono="aviso" />
                          </td>
                          <td className="numero apagado">—</td>
                        </>
                      ) : (
                        <>
                          <td className="numero">{soles(r.cost)}</td>
                          <td className="numero">
                            {r.possiblePortions === 0 ? (
                              <span className="negativo mono">0</span>
                            ) : (
                              <span className="mono apagado">
                                {r.possiblePortions}
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="celda-nivel">
                              <span className="mono">
                                {porcentaje(r.foodCost)}
                              </span>
                              {r.ingredientsWithoutCost > 0 ? (
                                <Pastilla texto="incompleto" tono="aviso" />
                              ) : (
                                <Pastilla {...pastillaDeFoodCost(r.foodCost)} />
                              )}
                            </div>
                          </td>
                          <td
                            className={`numero ${
                              Number.parseFloat(r.margin) < 0 ? 'negativo' : ''
                            }`}
                          >
                            {soles(r.margin)}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {receta === undefined ? null : (
              <FichaTecnica
                receta={receta}
                objetivo={objetivo}
                onObjetivo={setObjetivo}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

function FichaTecnica({
  receta,
  objetivo,
  onObjetivo,
}: {
  receta: Recipe;
  objetivo: number;
  onObjetivo: (valor: number) => void;
}) {
  if (receta.lines.length === 0) {
    return (
      <Tarjeta>
        <p className="versal">Ficha técnica</p>
        <h2 className="tarjeta__titulo" style={{ marginTop: 'var(--hueco-xs)' }}>
          {receta.name}
        </h2>
        <p className="aviso-caja" style={{ marginBottom: 'var(--hueco-md)' }}>
          Este plato nació en la carta con la receta vacía. Mientras siga así,
          venderlo no descuenta nada del almacén y no hay costo ni margen que
          enseñar: cuesta cero, y eso no es verdad.
        </p>
        {receta.productId === null ? null : (
          <NuevaReceta
            productoInicial={receta.productId}
            texto="Llenar la receta"
          />
        )}
      </Tarjeta>
    );
  }

  return (
    <Tarjeta>
      <p className="versal">Ficha técnica</p>
      <h2 className="tarjeta__titulo" style={{ marginTop: 'var(--hueco-xs)' }}>
        {receta.name}
      </h2>
      <p className="nota-sm ficha__precio">
        Precio de carta {soles(receta.price)} · rinde{' '}
        {cantidad(receta.yieldQuantity)}
      </p>

      {/*
       * Cambiar el gramaje de un ingrediente pasaba por abrir «Nueva receta»
       * vacía; guardar ahí reemplazaba la receta entera con solo lo tecleado.
       * Desde la ficha, el formulario trae lo que ya tenía.
       */}
      {receta.productId === null ? null : (
        <NuevaReceta productoInicial={receta.productId} texto="Editar receta" />
      )}

      <ul className="lista">
        {receta.lines.map((line) => (
          <li key={line.supplyId} className="lista__fila">
            <div className="lista__texto">
              <strong>{line.supply}</strong>
              <span className="apagado mono">
                {cantidad(line.quantity)} {line.unit} × {soles(line.unitCost)}
              </span>
            </div>
            {line.hasCost ? (
              <span className="mono">{soles(line.lineCost)}</span>
            ) : (
              <Pastilla texto="sin costo" tono="aviso" />
            )}
          </li>
        ))}
      </ul>

      <dl className="resumen-pares">
        <div>
          <dt>Costo de insumos</dt>
          <dd className="mono">{soles(receta.ingredientsCost)}</dd>
        </div>
        <div>
          {/*
           * La merma va dentro del costo, no al lado: lo que se queda en la
           * olla se pagó igual que lo que llegó al plato.
           */}
          <dt>Merma estimada {receta.wastePercentage} %</dt>
          <dd className="mono">{soles(receta.wasteCost)}</dd>
        </div>
        <div>
          <dt>
            <strong>Costo del plato</strong>
          </dt>
          <dd className="mono">
            <strong>{soles(receta.cost)}</strong>
          </dd>
        </div>
      </dl>

      {receta.ingredientsWithoutCost > 0 ? (
        <p className="aviso-caja" style={{ marginBottom: 'var(--hueco-md)' }}>
          {receta.ingredientsWithoutCost === 1
            ? 'Un ingrediente todavía no tiene costo'
            : `${receta.ingredientsWithoutCost} ingredientes todavía no tienen costo`}
          : nunca han entrado al almacén. El costo del plato sale más bajo de lo
          real y el margen que se ve aquí no se puede creer todavía. Se arregla
          recibiéndolos una vez.
        </p>
      ) : null}

      <Simulador
        receta={receta}
        objetivo={objetivo}
        onObjetivo={onObjetivo}
      />
    </Tarjeta>
  );
}

/**
 * Qué precio tendría que tener este plato, y qué pasa si no lo tiene.
 *
 * La versión anterior enseñaba un «precio sugerido» y nada más: un número sin
 * de dónde salía ni qué hacer con él. Quien no sabe qué es el food cost no
 * sabía si 52,30 era bueno o malo, y quien sí lo sabe tenía que restar de
 * cabeza para ver cuánto le faltaba.
 *
 * Ahora dice las dos cosas que se pueden hacer, con su número cada una: subir
 * el precio o bajar el costo. Son palancas distintas —una la decide el dueño,
 * la otra la cocina— y casi nunca se mueve solo una.
 */
function Simulador({
  receta,
  objetivo,
  onObjetivo,
}: {
  receta: Recipe;
  objetivo: number;
  onObjetivo: (valor: number) => void;
}) {
  const costo = Number.parseFloat(receta.cost);
  const precio = Number.parseFloat(receta.price);

  const precioParaLaMeta = objetivo <= 0 ? 0 : costo / (objetivo / 100);
  const costoParaLaMeta = (precio * objetivo) / 100;

  const subir = precioParaLaMeta - precio;
  const bajar = costo - costoParaLaMeta;
  const enMeta = receta.foodCost <= objetivo;

  return (
    <>
      <p className="versal" style={{ marginBottom: 'var(--hueco-xs)' }}>
        Simular precio de venta
      </p>
      <p className="nota-sm">
        {/*
         * La definición va aquí y no en una ayuda escondida: el food cost es
         * el número que manda en esta pantalla y hay encargados que lo ven por
         * primera vez el día que se les da el almacén.
         */}
        El <strong>food cost</strong> es qué parte del precio se va en insumos.
        Cuanto más alto, menos queda para sueldos, alquiler y ganancia. La meta
        la pone cada carta; treinta por ciento es lo habitual en un restaurante.
      </p>

      <div className="filtros" style={{ marginBottom: 'var(--hueco-md)' }}>
        {OBJETIVOS.map((valor) => (
          <Filtro
            key={valor}
            texto={`Meta ${valor} %`}
            activo={objetivo === valor}
            onClick={() => onObjetivo(valor)}
          />
        ))}
      </div>

      {precio <= 0 ? (
        <p className="aviso-caja">
          Este plato no tiene precio en la carta, así que no hay food cost que
          calcular. El costo de sus insumos es {soles(receta.cost)}.
        </p>
      ) : enMeta ? (
        <div className="sugerido">
          <span className="versal">En meta</span>
          <strong className="mono">{porcentaje(receta.foodCost)}</strong>
        </div>
      ) : (
        <>
          <p className="aviso-caja" style={{ marginBottom: 'var(--hueco-sm)' }}>
            A {soles(receta.price)} este plato va al{' '}
            <strong>{porcentaje(receta.foodCost)}</strong>, o sea{' '}
            {puntos(receta.foodCost - objetivo)} puntos sobre la meta.
            Cada uno que sale deja {soles(receta.margin)} en vez de{' '}
            {soles(precio - costoParaLaMeta)}.
          </p>

          <p className="nota-sm">
            Para llegar al {objetivo} % hay dos caminos, y no hace falta elegir
            solo uno:
          </p>

          <ul className="lista lista--palancas">
            <li className="lista__fila">
              <div className="lista__texto">
                <strong>Subir el precio a {soles(precioParaLaMeta)}</strong>
                <span className="apagado">
                  {soles(subir)} más de lo que cuesta hoy en la carta
                </span>
              </div>
            </li>
            <li className="lista__fila">
              <div className="lista__texto">
                <strong>
                  O bajar el costo a {soles(costoParaLaMeta)} sin tocar el
                  precio
                </strong>
                <span className="apagado">
                  {soles(bajar)} menos por plato: gramaje, merma o a quién se
                  le compra
                </span>
              </div>
            </li>
          </ul>
        </>
      )}
    </>
  );
}

/**
 * Lo que se vende y todavía no descuenta del almacén.
 *
 * Es la misma tarjeta para las dos deudas porque la forma es la misma —qué
 * falta, cuántas veces se vendió sin ello, y el botón que lo resuelve— y lo
 * único que cambia es cómo se salda.
 */
function Deuda({
  titulo,
  explicacion,
  etiqueta,
  items,
  accion,
}: {
  titulo: string;
  explicacion: string;
  etiqueta: string;
  items: PendingRecipe[];
  accion: ReactNode;
}) {
  return (
    <Tarjeta>
      <div className="tarjeta__cabeza">
        <h2 className="tarjeta__titulo">
          {titulo} <span className="mono apagado">({items.length})</span>
        </h2>
        {accion}
      </div>
      <p className="nota">
        {explicacion}
      </p>
      <ul className="lista">
        {items.map((item) => (
          <li key={item.productId} className="lista__fila">
            <div className="lista__texto">
              <strong>{item.productName}</strong>
              <span className="apagado">
                Vendido {contados(item.timesSold, 'vez', 'veces')} sin descontar
              </span>
            </div>
            <Pastilla texto={etiqueta} tono="peligro" />
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}

/**
 * Los tres tramos del food cost.
 *
 * Sobre meta es rojo porque un plato que se come el margen cuesta dinero todos
 * los días. Es la diferencia con el sobrestock del almacén, que solo ocupa
 * sitio y por eso no se pinta de alarma.
 */
/**
 * Los extras de la carta y si salen del almacén.
 *
 * «Doble pollo», «con huevo», «agrandado»: se cobran aparte y llevan
 * ingredientes. Sin receta no descuentan nada, y ese pollo de más es justo el
 * que el conteo da por perdido. Ver la migración 181.
 */
function Extras({ extras }: { extras: ExtraRecipe[] }) {
  const sinReceta = extras.filter((extra) => (extra.recipe?.lines.length ?? 0) === 0);

  return (
    <Tarjeta>
      <h2 className="tarjeta__titulo">
        Extras <span className="mono apagado">({extras.length})</span>
      </h2>
      <p className="nota">
        Lo que se cobra aparte y también sale del almacén. Lo que lleve cada extra se suma a lo
        del plato al vender.
        {sinReceta.length > 0
          ? ` ${contados(sinReceta.length, 'extra', 'extras')} sin receta: se cobran y no descuentan nada.`
          : ''}
      </p>
      <ul className="lista">
        {extras.map((extra) => {
          const lleva = extra.recipe?.lines ?? [];
          return (
            <li key={extra.optionId} className="lista__fila">
              <div className="lista__texto">
                <strong>{extra.optionName}</strong>
                <span className="apagado">
                  {extra.groupName}
                  {Number.parseFloat(extra.priceDelta) > 0
                    ? ` · ${soles(Number.parseFloat(extra.priceDelta))}`
                    : ''}
                  {lleva.length > 0
                    ? ` · lleva ${lleva.map((linea) => linea.supply).join(', ')}`
                    : ''}
                </span>
              </div>
              {lleva.length === 0 ? <Pastilla texto="Sin receta" tono="aviso" /> : null}
              <NuevaReceta
                extra={extra}
                texto={lleva.length === 0 ? 'Cargar receta' : 'Editar receta'}
              />
            </li>
          );
        })}
      </ul>
    </Tarjeta>
  );
}

function pastillaDeFoodCost(valor: number) {
  if (valor > 33) return { texto: 'Sobre meta', tono: 'peligro' as const };
  if (valor > 30) return { texto: 'Al límite', tono: 'aviso' as const };

  return { texto: 'En meta', tono: 'exito' as const };
}
